import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTonAddress } from '@tonconnect/ui-react';
import { useNotifications } from '../hooks/useNotifications';
import type { InAppNotification } from '../lib/api';

function timeAgo(dateStr: string): string {
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const EVENT_ICONS: Record<string, string> = {
  offer_received: '\u{1F4E9}',
  offer_accepted: '\u2705',
  offer_rejected: '\u274C',
  lot_cancelled: '\u{1F6AB}',
  lot_expiring: '\u23F0',
  trade_completed: '\u{1F91D}',
};

function NotifItem({
  notif,
  onNavigate,
}: {
  notif: InAppNotification;
  onNavigate: (lotId: number) => void;
}) {
  const icon = EVENT_ICONS[notif.eventType] || '\u{1F514}';

  return (
    <button
      onClick={() => notif.lotId && onNavigate(notif.lotId)}
      className={`w-full text-left px-3 py-2.5 flex gap-2.5 items-start transition-colors hover:bg-surface-alt ${
        notif.read ? 'opacity-60' : ''
      }`}
    >
      <span className="text-base mt-0.5 shrink-0">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="text-sm font-semibold truncate">{notif.title}</span>
          {!notif.read && (
            <span className="w-1.5 h-1.5 rounded-full bg-cherry-500 shrink-0" />
          )}
        </div>
        <p className="text-xs text-text-secondary mt-0.5 line-clamp-2">{notif.message}</p>
        <span className="text-[0.625rem] text-text-muted mt-0.5 block">
          {timeAgo(notif.createdAt)}
        </span>
      </div>
    </button>
  );
}

export function NotificationBell() {
  const address = useTonAddress();
  const { notifications, unreadCount, markAllRead, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  if (!address) return null;

  const handleNavigate = (lotId: number) => {
    setOpen(false);
    navigate(`/lot/${lotId}`);
  };

  return (
    <div className="relative" ref={panelRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-1.5 rounded-lg hover:bg-surface-alt transition-colors text-text-muted"
        aria-label="Notifications"
      >
        <svg className="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-4 h-4 flex items-center justify-center rounded-full bg-cherry-500 text-white text-[0.6rem] font-bold px-1 leading-none">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Notifications"
          className="fixed inset-x-3 top-14 sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-1 sm:w-80 max-h-112 bg-surface border border-border rounded-xl shadow-(--shadow-modal) overflow-hidden z-50 animate-in fade-in slide-in-from-top-1"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
            <span className="text-sm font-bold">Notifications</span>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={() => void markAllRead()}
                  className="text-xs text-ton-500 hover:text-ton-600 font-medium"
                >
                  Mark all read
                </button>
              )}
              {notifications.length > 0 && (
                <button
                  onClick={() => void clearAll()}
                  className="text-xs text-text-muted hover:text-error font-medium"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="overflow-y-auto max-h-96 divide-y divide-border">
            {notifications.length === 0 ? (
              <div className="py-8 text-center text-text-muted text-sm">
                No notifications yet
              </div>
            ) : (
              notifications.map((n) => (
                <NotifItem key={n.id} notif={n} onNavigate={handleNavigate} />
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
